import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import axios from '../../../dataClient/dataClient'
import { SecondaryButton, IconOnlyButton } from '../../common/Button'
import Loading from '../../common/Loading'
import LoadingError from '../../common/LoadingError'
import { ErrorText } from '../../styles/Text'
import { handleDownloadResponse } from '../../../util/downloadFile'
import ContinuousPdfPages, { LazyPdfPage } from './ContinuousPdfPages'
import ZoomPageToolbar from './ZoomPageToolbar'
import usePdfDocument from '../../../hooks/redaction/usePdfDocument'
import usePageParam from '../../../hooks/redaction/usePageParam'
import usePageZoom from '../../../hooks/redaction/usePageZoom'
import { backToRequestUrl } from './paths'
import { countAppliedRedactions } from '../../../util/redaction/annotationMath'
import {
  Shell,
  HeaderBar,
  HeaderTitles,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  Toolbar,
  ToolDivider,
  ToolbarSpacer,
  Body,
  CanvasScroll,
  Rail,
  RailTitle,
  HelperText,
  ViewOnlyPill,
  InfoLine,
  PageTile,
  PageTileList,
  Thumb
} from './editorChrome'

const THUMB_WIDTH_PX = 44
const FILE_LABELS = {
  COMBINED: { title: 'original', info: 'This is the combined original PDF (no redactions). Suggest or edit redactions from the request page.' },
  REDACTIONS_NOTED: { title: 'redactions noted', info: 'Open the redaction editor to change these boxes.' },
  REDACTIONS_APPLIED: {
    title: 'redacted',
    info: 'Redactions are burned into this file. To change them, edit the redactions-noted PDF and apply again.'
  },
  REDACTION_REASONS: { title: 'redaction reasons', info: 'Generated when redactions were applied; lists every burned box with its reason.' }
}

// Read-only viewer for generated redaction files.
function RedactionViewerPage() {
  const { foiId, categoryId, fileId } = useParams()
  const navigate = useNavigate()

  const [category, setCategory] = useState(null)
  const [file, setFile] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(false)
  const [appliedCount, setAppliedCount] = useState(null)
  const [railElement, setRailElement] = useState(null)
  const activeTileRef = useRef(null)

  const { pages, loading: pdfLoading, error: pdfError, version } = usePdfDocument(file?.id)

  const pageCount = pages.length
  const page = usePageParam(pageCount)
  const pageWidthPoints = pages.length ? Math.max(...pages.map(pdfPage => pdfPage.getViewport({ scale: 1 }).width)) : 612
  const zoom = usePageZoom({ pageWidthPoints, pdfLoading })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [{ data: categoryRes }, { data: filesRes }] = await Promise.all([
          axios.get(`/category/${categoryId}`),
          axios.get(`/category/${categoryId}/files`)
        ])
        if (cancelled) return
        const loadedCategory = categoryRes.data
        if (loadedCategory.foiId && loadedCategory.foiId !== foiId) {
          setLoadError(['This category does not belong to the requested FOI request.'])
          return
        }
        const found = (filesRes.data.files ?? []).find(f => f.id === fileId)
        if (!found) {
          setLoadError(['The requested file does not exist in this category.'])
          return
        }
        setCategory(loadedCategory)
        setFile(found)
      } catch (e) {
        if (cancelled) return
        console.error('RedactionViewerPage: failed to load', e)
        setLoadError([
          e.response?.status === 404
            ? 'The requested category does not exist.'
            : 'There was an error loading the file viewer. Please refresh the page to try again.'
        ])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [foiId, categoryId, fileId])

  // Applied files store redaction counts in their text labels.
  useEffect(() => {
    if (file?.status !== 'REDACTIONS_APPLIED' || pages.length === 0) return undefined
    let cancelled = false
    countAppliedRedactions(pages)
      .then(count => {
        if (!cancelled) setAppliedCount(count)
      })
      .catch(e => console.error('RedactionViewerPage: could not count applied redactions', e))
    return () => {
      cancelled = true
    }
  }, [file?.status, pages])

  useEffect(() => {
    activeTileRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [page.pageIndex])

  function handleDownload() {
    setDownloadError(false)
    setDownloading(true)
    axios
      .get(`/file/${file.id}/download`, { responseType: 'blob' })
      .then(response => handleDownloadResponse(response, file.filename, 'download'))
      .catch(e => {
        setDownloadError(true)
        console.error('RedactionViewerPage: download failed', file.filename, e)
      })
      .finally(() => setDownloading(false))
  }

  if (loadError) {
    return (
      <Shell>
        <HeaderBar>
          <IconOnlyButton aria-label="Back to request" onClick={() => navigate(backToRequestUrl(foiId, categoryId))}>
            <ArrowBackIcon />
          </IconOnlyButton>
          <HeaderTitles>
            <HeaderTitle>File viewer</HeaderTitle>
          </HeaderTitles>
        </HeaderBar>
        <LoadingError message={loadError} />
      </Shell>
    )
  }

  if (!category || !file || (pdfLoading && version === 0)) {
    return (
      <Shell>
        <Loading />
      </Shell>
    )
  }

  if (pdfError && version === 0) {
    return (
      <Shell>
        <LoadingError message={['The PDF could not be opened. Please refresh the page to try again.']} />
      </Shell>
    )
  }

  const labels = FILE_LABELS[file.status] ?? { title: file.status.toLowerCase(), info: '' }
  const appliedSuffix =
    file.status === 'REDACTIONS_APPLIED' && appliedCount !== null
      ? ` · ${appliedCount} ${appliedCount === 1 ? 'redaction' : 'redactions'} applied`
      : ''
  const subtitle = `${file.filename} · ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}${appliedSuffix}`

  return (
    <Shell data-testid="redaction-viewer">
      <HeaderBar>
        <IconOnlyButton aria-label="Back to request" onClick={() => navigate(backToRequestUrl(foiId, categoryId))}>
          <ArrowBackIcon />
        </IconOnlyButton>
        <HeaderTitles>
          <HeaderTitle title={`${category.name} — ${labels.title}`}>
            {category.name} — {labels.title}
          </HeaderTitle>
          <HeaderSubtitle data-testid="viewer-subtitle" title={subtitle}>
            {subtitle}
          </HeaderSubtitle>
        </HeaderTitles>
        <ViewOnlyPill data-testid="view-only-pill">
          <LockOutlinedIcon /> View only
        </ViewOnlyPill>
        <HeaderActions>
          <SecondaryButton onClick={handleDownload} loading={downloading} padding="short" aria-label="Download File">
            <DownloadIcon /> Download
          </SecondaryButton>
          {downloadError && <ErrorText role="alert">Error, try again.</ErrorText>}
        </HeaderActions>
      </HeaderBar>

      <Toolbar>
        {labels.info && (
          <>
            <InfoLine data-testid="viewer-info">
              <InfoOutlinedIcon /> {labels.info}
            </InfoLine>
            <ToolDivider />
          </>
        )}
        <ZoomPageToolbar zoom={zoom} page={page} pageCount={pageCount} />
        <ToolbarSpacer />
      </Toolbar>

      <Body>
        <CanvasScroll ref={zoom.scrollRef} data-testid="viewer-canvas">
          <ContinuousPdfPages
            pages={pages}
            scale={zoom.scale}
            pageIndex={page.pageIndex}
            navigationKey={page.navigationKey}
            scrollRef={zoom.scrollRef}
            onPageChange={page.setVisiblePage}
          />
        </CanvasScroll>
        <Rail as="aside" ref={setRailElement} data-testid="page-rail">
          <RailTitle>Pages</RailTitle>
          <HelperText>Jump to a page or use the page box in the toolbar. Opened from search: lands on the matching page.</HelperText>
          <PageTileList>
            {pages.map((railPage, index) => (
              <PageTile
                key={index}
                ref={index === page.pageIndex ? activeTileRef : undefined}
                active={index === page.pageIndex ? 'true' : 'false'}
                onClick={() => page.goToPage(index + 1)}
                aria-current={index === page.pageIndex ? 'page' : undefined}>
                <Thumb aria-hidden="true">
                  <LazyPdfPage
                    page={railPage}
                    pageIndex={index}
                    scale={THUMB_WIDTH_PX / railPage.getViewport({ scale: 1 }).width}
                    active={index === page.pageIndex}
                    scrollRoot={railElement}
                    testId="pdf-thumb"
                  />
                </Thumb>
                Page {index + 1}
              </PageTile>
            ))}
          </PageTileList>
        </Rail>
      </Body>
    </Shell>
  )
}

export default RedactionViewerPage
